/**
 * Web Component entry point for the Pioneer Point Status widget.
 *
 * Importing this module registers the <legion-pioneer-point-status> custom
 * element so it can be used in any HTML page or framework that supports web
 * components.
 *
 * @example
 * ```ts
 * import '@legionhandtech/lht-react-widgets/pioneer-point-status-web-component';
 *
 * // Then use in HTML:
 * // <legion-pioneer-point-status api-url="https://your-domain.com" auth-token="jwt" />
 * ```
 */

import {
  attachAuthBridge,
  type AuthBridgeHandle,
  AUTH_BRIDGE_OBSERVED_ATTRS,
} from "../shared/auth-bridge";

class LegionPioneerPointStatusElement extends HTMLElement {
  public iframe: HTMLIFrameElement | null = null;
  private authBridge: AuthBridgeHandle | null = null;

  constructor() {
    super();
    this.attachShadow({ mode: "open" });
  }

  static get observedAttributes(): string[] {
    return [
      "api-url",
      "width",
      "height",
      "theme",
      ...AUTH_BRIDGE_OBSERVED_ATTRS,
    ];
  }

  connectedCallback(): void {
    this.authBridge = attachAuthBridge(this, {
      iframeAccessor: () => this.iframe,
      baseUrl: this.bridgeBaseUrl,
    });
    this.render();
  }

  disconnectedCallback(): void {
    this.authBridge?.detach();
    this.authBridge = null;
  }

  attributeChangedCallback(
    _name: string,
    oldValue: string | null,
    newValue: string | null
  ): void {
    if (oldValue !== newValue) {
      this.render();
    }
  }

  private get apiUrl(): string {
    return this.getAttribute("api-url") || window.location.origin;
  }

  private get widgetWidth(): string {
    return this.getAttribute("width") || "100%";
  }

  private get widgetHeight(): string {
    return this.getAttribute("height") || "180";
  }

  private get theme(): string {
    return this.getAttribute("theme") || "light";
  }

  render(): void {
    if (!this.shadowRoot) return;

    const iframe = document.createElement("iframe");
    const url = new URL("/widget/pioneer-point-status", this.apiUrl);
    url.searchParams.set("theme", this.theme);

    iframe.src = url.toString();
    iframe.width = "100%";
    iframe.height = this.widgetHeight;
    iframe.frameBorder = "0";
    iframe.scrolling = "no";
    iframe.title = "Pioneer Point Status Widget";

    iframe.onload = () => {
      this.authBridge?.postCurrentToken();
      this.dispatchEvent(
        new CustomEvent("load", {
          detail: { widget: "pioneer-point-status", theme: this.theme },
        })
      );
    };

    iframe.onerror = () => {
      if (!this.shadowRoot) return;
      this.shadowRoot.innerHTML = `<div style="padding:32px;text-align:center;color:#856404;background:#fff3cd;border:1px solid #ffeaa7;border-radius:8px;">Failed to load pioneer point status widget</div>`;
    };

    const style = document.createElement("style");
    style.textContent = `
      :host {
        display: block;
        width: ${this.widgetWidth};
      }
      iframe {
        width: 100%;
        border: none;
        background: ${this.theme === "dark" ? "#1a1a1a" : "white"};
      }
    `;

    this.shadowRoot.innerHTML = "";
    this.shadowRoot.appendChild(style);
    this.shadowRoot.appendChild(iframe);
    this.iframe = iframe;
  }

  private get bridgeBaseUrl(): string {
    return this.apiUrl;
  }
}

if (
  typeof customElements !== "undefined" &&
  !customElements.get("legion-pioneer-point-status")
) {
  customElements.define(
    "legion-pioneer-point-status",
    LegionPioneerPointStatusElement
  );
}

export { LegionPioneerPointStatusElement };
